<template>
  <div class="home">
    <div class="home__hero">
      <HeroBackgroundSlider />
    </div>

    <descriptionArea
      :title="$t('home.desc1Title')"
      :description="$t('home.desc1Desc')"/>

    <swiperProducts
      :overlineTitle="$t('home.overlineTitle')"/>

    <descriptionArea
      :italic="$t('home.desc2Title')"
      :description="$t('home.desc2Desc')"/>

    <div
      :class="{'home-category-listings--isTouch': touch}"
      class="home-category-listings">
      <CategoriesList
        v-if="typeof chunkArray[0] !== 'undefined'"
        :list="chunkArray[0]" />
      <CategoriesList
        v-if="typeof chunkArray[1] !== 'undefined'"
        :list="chunkArray[1]"
        :rellaxSpeed="1" />
      <CategoriesList
        v-if="typeof chunkArray[2] !== 'undefined'"
        :list="chunkArray[2]" />
    </div>

    <textImageFF
      :title="$t('home.textImageTitle')"
      :description="$t('home.textImageDescription')"
      :image="textImageImage"
      :imageMobile="textImageImageMobile"
      :textButton="$t('home.textImageTextButton')"
      :link="textImageLink"
      :loading="loading"/>

    <textImageParallax
      :title="$t('home.textParallaxTitle')"
      :title2="$t('home.textParallaxTitle2')"
      :title3="$t('home.textParallaxTitle3')"
      :image="textParallaxImage"
      :textButton="$t('home.textParallaxTextButton')"
      :link="textParallaxLink"
      :loading="loading"/>

    <swiperPosts
      :overlineTitle="'Glaxi<br/> Academy'"
      :subTitle="$t('academy.blockSubtitle')"/>

    <glaxiFooter/>

    <NewsPopUp v-if="!loading" :viewPort="getViewPort" :cookieConsent="cookieConsent"/>
    <englishLayer/>

  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { splitEvery, transpose } from 'ramda'
import pageSeo from './seo.js'
// @ is an alias to /src
import HeroBackgroundSlider from '@/components/HeroBackgroundSlider/HeroBackgroundSlider.vue'
import NewsPopUp from '@/components/NewsPopUp/NewsPopUp.vue'
import DescriptionArea from '@/components/DescriptionArea/DescriptionArea.vue'
import SwiperProducts from '@/components/SwiperProducts/SwiperProducts.vue'
import CategoriesList from '@/components/CategoriesList/CategoriesList.vue'
import TextImageFF from '@/components/TextImageFF/TextImageFF.vue'
import TextImageParallax from '@/components/TextImageParallax/TextImageParallax.vue'
import SwiperPosts from '@/components/SwiperPosts/SwiperPosts.vue'
import GlaxiFooter from '@/components/GlaxiFooter/GlaxiFooter.vue'
import EnglishLayer from '@/components/EnglishLayer/EnglishLayer.vue'

import matchMedia from '@/mixins/matchMedia.js'
import getSinglePages from '@/mixins/getSinglePages.js'

export default {
  name: 'home',
  mixins: [matchMedia, getSinglePages],
  components: {
    HeroBackgroundSlider,
    NewsPopUp,
    DescriptionArea,
    SwiperProducts,
    CategoriesList,
    TextImageFF,
    TextImageParallax,
    SwiperPosts,
    GlaxiFooter,
    EnglishLayer
  },
  props: {
    cookieConsent: {
      type: Boolean,
      required: true
    }
  },
  data: () => ({
    pageSeo: pageSeo,
    loading: true,
    textImageImage: require('@/assets/home/famigliaGlaxi.jpg'),
    textImageImageMobile: require('@/assets/home/famigliaGlaxiMobile.jpg'),
    textParallaxImage: require('@/assets/home/surgelazione.jpg')
  }),
  computed: {
    ...mapGetters('contentful', [
      'categories'
    ]),
    ...mapGetters([
      'touch',
      'lang'
    ]),
    getViewPort: {
      get () {
        return this.matchMedia === 'medium' || this.matchMedia === 'small'
      }
    },
    currentCategory: {
      get () {
        if (this.loading) return {}
        return this.category(this.slug)
      }
    },
    chunkArray: {
      get () {
        if (this.loading) return [[], [], []]
        if (this.matchMedia === 'medium' || this.matchMedia === 'small')
          return transpose(splitEvery(2, this.categories))
        return transpose(splitEvery(3, this.categories))
      }
    },
    textImageLink: {
      get () {
        return {
          name: `${this.lang}.single-page`,
          params: { slug: this.$_getPageSlug('Services') }
        }
      }
    },
    textParallaxLink: {
      get () {
        return { 
          name: `${this.lang}.single-page`,
          params: { slug: this.$_getPageSlug('Company') } 
        }
      } 
    }
  },
  beforeRouteEnter (from, to, next) {
    next(vm => {
      vm.loadData()
    })
  },
  beforeRouteUpdate (from, to, next) {
    next(vm => {
      vm.loadData()
    })
  },
  methods: {
    ...mapActions('contentful', [
      'loadCategories'
    ]),
    async loadData () {
      this.loading = true
      try {
        await Promise.all([this.loadCategories()])
        this.loading = false
      } catch (e) {
        // TODO sentry
      }
    }
  },
  metaInfo () {
    return {
      title: this.pageSeo[this.lang].title,
      meta: [
        { vmid: 'description', name: 'description', content: this.pageSeo[this.lang].description },
        { property: 'og:locale', content: this.lang },
        { property: 'og:title', content: this.pageSeo[this.lang].title },
        { property: 'og:description', content: this.pageSeo[this.lang].description },
        { property: 'og:url', content: this.pageSeo[this.lang].hreflang }
        // { property: 'og:image', content: this.content.images.background.xlarge }
      ],
      link: [
        { rel: 'alternate', href: `${window.location.origin}${this.pageSeo['it'].hreflang}`, hreflang: 'it' },
        { rel: 'alternate', href: `${window.location.origin}${this.pageSeo['en'].hreflang}`, hreflang: 'en' },
        { rel: 'canonical', href: location.protocol + '//' + location.host + location.pathname }
      ]
    }
  }
}
</script>

<style lang="scss">
  @import "./style.scss";
</style>
